import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { provideNgxMask } from 'ngx-mask';
import { CONSTANTS } from "../../../../Constants/Constants";
import { CommonDataService } from "../../../../services/common-data.service";
import { SolCalculationsService } from "../../../../services/operations/sol-calculations.service";
import { TranslatePipe } from "@ngx-translate/core";
import { SubsidyMaterialType } from "../../../../models/dtos";
import { CommonMetaDataService } from "../../../../services/common-meta-data.service";
import { TrackChangesDirective } from "../../../../directives/track-changes.directive";
import { NumericFieldConfig } from "../../../../shared/numeric/numeric.types";
import { NumericMaskDirective } from "../../../../shared/numeric/numeric-mask.directive";
import { Subject, takeUntil } from "rxjs";
@Component({
    selector: 'app-sol-subsidy-program',
    templateUrl: './sol-subsidy-program.component.html',
    styleUrls: ['./sol-subsidy-program.component.scss'],
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        TranslatePipe,
        TrackChangesDirective,
        NumericMaskDirective
    ],
    providers: [
        ...provideNgxMask()
    ]
})
export class SolSubsidyProgramComponent implements OnInit, OnDestroy {
    @Output() cancel = new EventEmitter<void>();
    @Input() isEstimateSapSubmitted: boolean = false;
    private readonly destroy$ = new Subject<void>();
    isAppReadOnly = false;

    get isReadOnly(): boolean {
        return this.isEstimateSapSubmitted || this.isAppReadOnly;
    }
    showApplyBtn: boolean = true;
    applyToList: SubsidyMaterialType[] = [];
    subsidyProgramForm: FormGroup<{
        applyToDropdown: FormControl<number>;
        subsidyDollars: FormControl<number>;
    }> = new FormGroup({
        applyToDropdown: new FormControl(CONSTANTS.ONE),
        subsidyDollars: new FormControl(CONSTANTS.ZERO)
    });

    readonly subsidyDollarsConfig: NumericFieldConfig = {
        kind: 'currency',
        scale: CONSTANTS.TWO,
        min: CONSTANTS.ZERO,
        max: CONSTANTS.SUBSIDY_DOLLARS_MAX,
        useGrouping: true,
        showStepper: false,
        step: CONSTANTS.ONE,
        revertToLastValidValue: true
    }
    constructor(
        private readonly commonDataService: CommonDataService,
        private readonly commonMetaDataService: CommonMetaDataService,
        private readonly solCalculationsService: SolCalculationsService
    ) { }

    trackByApplyToValue(index: number, item: SubsidyMaterialType): number {
        return item.id;
    }

    ngOnInit() {
        this.prefillSubsidyProgramForm();
        this.applyToList = this.commonMetaDataService.getMetaData()?.subsidyMaterialTypes || [];
        this.setupReadOnlyMode();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.isEstimateSapSubmitted = false;
    }

    private syncFormReadOnlyState(): void {
        if (this.isReadOnly || !this.showApplyBtn) {
            this.subsidyProgramForm.disable({ emitEvent: false });
        } else {
            this.subsidyProgramForm.enable({ emitEvent: false });
        }
    }
    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntil(this.destroy$))
            .subscribe(isReadOnly => {
                this.isAppReadOnly = isReadOnly;
                this.syncFormReadOnlyState();
            });
    }
    prefillSubsidyProgramForm(): void {
        const estimateTabData = this.commonDataService.getEstimateMetaData()?.estimateTab;
        if (estimateTabData && estimateTabData.subsidy && estimateTabData.subsidy > CONSTANTS.ZERO) {
            this.subsidyProgramForm.controls.subsidyDollars.patchValue(estimateTabData.subsidy);
            this.subsidyProgramForm.controls.applyToDropdown.patchValue(estimateTabData.subsidyMaterial);
            this.subsidyProgramForm.controls.subsidyDollars.disable();
            this.subsidyProgramForm.controls.applyToDropdown.disable();
            this.showApplyBtn = false;
        } else {
            this.showApplyBtn = true;
        }
    }

    async onApply() {
        if (!this.subsidyProgramForm || this.isReadOnly) {
            return;
        }
        this.showApplyBtn = false;
        if (this.subsidyProgramForm.controls.applyToDropdown.value) {
            const subsidyDollars = this.subsidyProgramForm.controls.subsidyDollars.value;
            const applyTo = this.subsidyProgramForm.controls.applyToDropdown.value;
            this.subsidyProgramForm.controls.subsidyDollars.disable();
            this.subsidyProgramForm.controls.applyToDropdown.disable();
            await this.saveSubsidyProgramData(subsidyDollars, applyTo);
        }
    }

    async onRemove() {
        if (!this.subsidyProgramForm || this.isReadOnly) {
            return;
        }
        this.showApplyBtn = true;
        this.subsidyProgramForm.controls.subsidyDollars.enable();
        this.subsidyProgramForm.controls.applyToDropdown.enable();
        await this.saveSubsidyProgramData(CONSTANTS.ZERO, CONSTANTS.ZERO);
    }

    async saveSubsidyProgramData(subsidyDollars: number, applyTo: number): Promise<void> {
        if (this.isReadOnly) {
            return;
        }

        const estimateMetaData = this.commonDataService.getEstimateMetaData();
        const estimateTabData = estimateMetaData?.estimateTab;
        if (estimateTabData) {
            estimateTabData.subsidy = subsidyDollars;
            estimateTabData.subsidyMaterial = applyTo;
            estimateMetaData.estimateTab = estimateTabData;
            this.commonDataService.patchEstimateMetaData(estimateMetaData);
            this.commonDataService.notifySubsidyChanged();
            await this.solCalculationsService.recalculateSubsidy();
        }
    }

    enableApplyBtn(): boolean {
        return !this.isReadOnly && this.subsidyProgramForm.controls.subsidyDollars.value > CONSTANTS.ZERO;
    }

    onClose(): void {
        this.cancel.emit();
    }
}